import type { MocXuatBan, TheChiSo } from '@qlk/shared';
import { ngayDayVn, ngayGonVn } from '@qlk/shared';

import { dinhDangGiaTri } from './TheChiSo.js';

/**
 * Biểu đồ đường có tô vùng cho một chỉ số theo ngày.
 *
 * Vẽ bằng SVG thuần (quyết định chốt số 5 — không thêm thư viện biểu đồ). Trục
 * giá trị **bên phải** như Studio, 4 vạch lưới, dải ▶ ở những ngày có video
 * xuất bản, cột của ngày **chưa chốt số liệu** tô xám.
 */

export type MauBieuDo = 'luot-xem' | 'noi-dung' | 'doi-tuong' | 'doanh-thu';

/*
 * Tông của từng tab, lấy từ token `--color-do-*` ở `theme.css`. SVG nhận giá
 * trị màu qua thuộc tính `stroke`/`fill`, không qua lớp Tailwind, nên đọc token
 * bằng `var()` — vẫn là một nguồn màu duy nhất, không gõ lại mã màu ở đây.
 */
const MAU: Record<MauBieuDo, { duong: string; vung: string }> = {
  'luot-xem': { duong: 'var(--color-do-xem)', vung: 'var(--color-do-xem-nen)' },
  'noi-dung': { duong: 'var(--color-do-noi-dung)', vung: 'var(--color-do-noi-dung-nen)' },
  'doi-tuong': { duong: 'var(--color-do-doi-tuong)', vung: 'var(--color-do-doi-tuong-nen)' },
  'doanh-thu': { duong: 'var(--color-do-tien)', vung: 'var(--color-do-tien-nen)' },
};

/*
 * Cỡ chữ của nhãn trục: **đơn vị user của SVG**, không phải px và không phải
 * token cỡ chữ. `viewBox` rộng 1000 bị co về bề ngang thật của khung, nên 13
 * đơn vị ở đây ra khoảng 9–11px trên màn hình. Lấy token `text-*` (px thật)
 * gán vào đây là chữ trục to gấp rưỡi.
 */
const CO_CHU_TRUC = 13;

const RONG = 1000;
const CAO = 240;
const LE_PHAI = 64;
const LE_DUOI = 28;
const LE_TREN = 12;

export interface BieuDoKyProps {
  the: TheChiSo;
  mocXuatBan: MocXuatBan[];
  mau: MauBieuDo;
  /** Nhãn trục ngày: 4 mốc đều nhau. */
  nhanKy?: string;
  /**
   * Hiện bảng số theo ngày.
   *
   * Luôn có trong DOM cho trình đọc màn hình; `true` thì hiện cho mọi người
   * (nút "Xem thêm").
   */
  moBang?: boolean;
}

/** Toạ độ x của điểm thứ `i` trong `n` điểm. */
export function toaDoX(i: number, n: number): number {
  if (n <= 1) return 0;
  return (i / (n - 1)) * (RONG - LE_PHAI);
}

/** Toạ độ y của `giaTri` trong khoảng 0..`caoNhat`. */
export function toaDoY(giaTri: number, caoNhat: number): number {
  if (caoNhat <= 0) return CAO - LE_DUOI;
  const ty = Math.min(Math.max(giaTri / caoNhat, 0), 1);
  return LE_TREN + (1 - ty) * (CAO - LE_DUOI - LE_TREN);
}

export function BieuDoKy({ the, mocXuatBan, mau, nhanKy, moBang = false }: BieuDoKyProps) {
  const diem = the.diem;

  if (diem.length === 0) {
    return (
      <div className="rounded-b-the border border-t-0 border-vien bg-be-mat p-6 text-co-ban text-chu-mo">
        Chưa có số liệu trong khoảng này.
      </div>
    );
  }

  const caoNhat = Math.max(...diem.map((d) => d.giaTri), 1);
  const bo = MAU[mau];

  const duongDan = diem
    .map(
      (d, i) =>
        `${i === 0 ? 'M' : 'L'} ${toaDoX(i, diem.length).toFixed(1)} ${toaDoY(d.giaTri, caoNhat).toFixed(1)}`,
    )
    .join(' ');

  const vung =
    `${duongDan} L ${toaDoX(diem.length - 1, diem.length).toFixed(1)} ${String(CAO - LE_DUOI)}` +
    ` L 0 ${String(CAO - LE_DUOI)} Z`;

  const theoNgay = new Map(mocXuatBan.map((m) => [m.date, m.title]));
  const vach = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="rounded-b-the border border-t-0 border-vien bg-be-mat p-4">
      <svg
        viewBox={`0 0 ${String(RONG)} ${String(CAO)}`}
        className="h-[240px] w-full"
        role="img"
        aria-label={`Biểu đồ ${the.nhan}${nhanKy ? ` ${nhanKy}` : ''}`}
      >
        {/* Bốn vạch lưới + trục giá trị bên phải */}
        {vach.map((v) => {
          const y = toaDoY(caoNhat * v, caoNhat);
          return (
            <g key={v}>
              <line
                x1={0}
                x2={RONG - LE_PHAI}
                y1={y}
                y2={y}
                stroke="var(--color-vien)"
                strokeWidth={1}
              />
              <text
                x={RONG - LE_PHAI + 8}
                y={y + 4}
                className="so-lieu"
                fontSize={CO_CHU_TRUC}
                fill="var(--color-chu-phu)"
              >
                {dinhDangGiaTri(caoNhat * v, the.dinhDang)}
              </text>
            </g>
          );
        })}

        {/* Cột xám cho ngày chưa chốt số liệu */}
        {diem.map((d, i) =>
          d.daChot ? null : (
            <rect
              key={`chua-chot-${d.date}`}
              x={toaDoX(i, diem.length) - 3}
              y={LE_TREN}
              width={6}
              height={CAO - LE_DUOI - LE_TREN}
              fill="var(--color-neutral-nen)"
            />
          ),
        )}

        <path d={vung} fill={bo.vung} />
        <path d={duongDan} fill="none" stroke={bo.duong} strokeWidth={2.5} />

        {/* Dải ▶ ở ngày có video xuất bản */}
        {diem.map((d, i) => {
          const tieuDe = theoNgay.get(d.date);
          if (!tieuDe) return null;

          return (
            <g key={`xuat-ban-${d.date}`}>
              <title>{`${tieuDe} · ${ngayDayVn(d.date)}`}</title>
              <rect
                x={toaDoX(i, diem.length) - 4}
                y={CAO - LE_DUOI + 4}
                width={8}
                height={10}
                rx={2}
                fill="var(--color-do-trong)"
              />
              <path
                d={`M ${(toaDoX(i, diem.length) - 1).toFixed(1)} ${String(CAO - LE_DUOI + 6)} l 4 3 l -4 3 z`}
                fill="var(--color-chu-phu)"
              />
            </g>
          );
        })}

        {/* Nhãn trục ngày: đầu, giữa, cuối */}
        {[...new Set([0, Math.floor(diem.length / 2), diem.length - 1])].map((i) => {
          const d = diem[i];
          if (!d) return null;
          return (
            <text
              key={`truc-${d.date}`}
              x={Math.min(Math.max(toaDoX(i, diem.length), 24), RONG - LE_PHAI - 24)}
              y={CAO - 4}
              fontSize={CO_CHU_TRUC}
              fill="var(--color-chu-phu)"
              textAnchor="middle"
            >
              {ngayGonVn(d.date)}
            </text>
          );
        })}
      </svg>

      {/*
        Bảng chữ cho trình đọc màn hình: SVG không đọc được số liệu.

        Lúc đóng, `sr-only` nằm trên **`div` bọc ngoài**, không trên `<table>`:
        hộp bảng bỏ qua `height: 1px` và `overflow: hidden` của `sr-only`, nên
        bảng ẩn vẫn cao thật, neo vào gốc trang và đẩy tài liệu dài hơn khung
        app — cuộn tới đáy là thấy một dải nền nhạt dưới app. Xem
        `e2e/93-mot-mo-hinh-cuon`.
      */}
      <div className={moBang ? undefined : 'sr-only'}>
        <table
          className={moBang ? 'mt-4 w-full border-t border-vien text-left text-co-ban' : undefined}
        >
          <caption className={moBang ? 'pb-2 pt-3 text-left text-phu text-chu-mo' : undefined}>
            {the.nhan} theo ngày
          </caption>
          <thead>
            <tr>
              <th scope="col" className={moBang ? 'py-1 text-phu text-chu-mo' : undefined}>
                Ngày
              </th>
              <th
                scope="col"
                className={moBang ? 'py-1 text-right text-phu text-chu-mo' : undefined}
              >
                {the.nhan}
              </th>
            </tr>
          </thead>
          <tbody>
            {diem.map((d) => (
              <tr
                key={d.date}
                className={moBang ? 'border-b border-vien last:border-0' : undefined}
              >
                <th scope="row" className={moBang ? 'py-1.5 font-normal text-chu-phu' : undefined}>
                  {ngayDayVn(d.date)}
                  {!d.daChot && moBang && <span className="ml-2 text-phu">(tạm tính)</span>}
                </th>
                <td className={moBang ? 'so-lieu py-1.5 text-right' : undefined}>
                  {dinhDangGiaTri(d.giaTri, the.dinhDang)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
